import { useEffect, useRef, type ReactNode } from 'react'
import { Icon } from './Icon'
import { Button } from './Button'

/**
 * Стек открытых модалок: Escape закрывает только верхнюю. Без него диалог
 * подтверждения, открытый поверх формы, закрывал бы и себя, и форму.
 */
const openStack: object[] = []

/** Модальное окно: шапка с крестиком, тело, подвал с кнопками. */
export function Modal({
  title,
  onClose,
  children,
  footer,
  wide
}: {
  title: string
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
  wide?: boolean
}): React.JSX.Element {
  const token = useRef({}).current

  useEffect(() => {
    openStack.push(token)
    return () => {
      const i = openStack.indexOf(token)
      if (i !== -1) openStack.splice(i, 1)
    }
  }, [token])

  // Закрытие по Escape — только для верхней модалки в стеке.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return
      if (openStack[openStack.length - 1] !== token) return
      onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose, token])

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className={`modal${wide ? ' modal--wide' : ''}`} role="dialog" aria-modal="true">
        <div className="modal__head">
          <div className="modal__title">{title}</div>
          <button className="modal__close" onClick={onClose} aria-label="Закрыть" type="button">
            {Icon.close}
          </button>
        </div>
        <div className="modal__body">{children}</div>
        {footer != null && <div className="modal__foot">{footer}</div>}
      </div>
    </div>
  )
}

/** Диалог подтверждения опасного действия. */
export function ConfirmDialog({
  title,
  message,
  confirmLabel = 'Удалить',
  busy,
  error,
  onConfirm,
  onCancel
}: {
  title: string
  message: string
  confirmLabel?: string
  busy?: boolean
  error?: string | null
  onConfirm: () => void
  onCancel: () => void
}): React.JSX.Element {
  return (
    <Modal
      title={title}
      onClose={onCancel}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            Отмена
          </Button>
          <Button variant="danger" onClick={onConfirm} busy={busy}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className="modal__text">{message}</p>
      {error && <div className="form-error form-error--gap">{error}</div>}
    </Modal>
  )
}
